// 📄 pages/Proyectos/modulosConfig.ts
// Módulos de un proyecto (docs/plans/plan-proyectos-generales.md, G1):
//   - "gestion": Gestión del proyecto, el núcleo que tiene TODO proyecto
//     (equipo, tareas, incidentes, documentación, pruebas, credenciales…).
//   - "sap-ariba": todo lo de SAP Ariba (soluciones, integración con el ERP,
//     facturación electrónica, catálogos ABAP, herramientas SAP).
// Fuente única: la usan el formulario de proyecto, el menú lateral y las
// rutas del proyecto. Agregar un módulo es agregar una entrada acá.

export type ModuloId = 'gestion' | 'sap-ariba';

export interface ModuloMeta {
  id: ModuloId;
  label: string;
  icon: string;
  descripcion: string;
  // El núcleo no se puede quitar.
  obligatorio?: boolean;
}

export const MODULOS: Record<ModuloId, ModuloMeta> = {
  gestion: {
    id: 'gestion',
    label: 'Gestión del proyecto',
    icon: '🗂️',
    descripcion: 'Equipo, tareas, incidentes, documentación, pruebas, credenciales y horas. Sirve para cualquier proyecto.',
    obligatorio: true
  },
  'sap-ariba': {
    id: 'sap-ariba',
    label: 'SAP Ariba',
    icon: '📦',
    descripcion: 'Soluciones Ariba, integración con el ERP (CIG), facturación electrónica, catálogos ABAP y herramientas SAP.'
  }
};

export const MODULO_OPTIONS = Object.values(MODULOS).map((m) => ({ value: m.id, label: m.label, icon: m.icon }));

// Proyectos creados antes de los módulos: eran todos SAP Ariba. Se tratan
// así en todo lector para que no cambie nada para ellos.
export const MODULOS_LEGADO: ModuloId[] = ['gestion', 'sap-ariba'];

export const modulosDe = (project?: { modulos?: string[] } | null): string[] => project?.modulos ?? MODULOS_LEGADO;
export const tieneModulo = (project: { modulos?: string[] } | null | undefined, modulo: ModuloId) => modulosDe(project).includes(modulo);
export const esProyectoSap = (project?: { modulos?: string[] } | null) => tieneModulo(project, 'sap-ariba');

export type PlantillaId = 'general' | 'software' | 'sap-ariba';

export interface PlantillaProyecto {
  id: PlantillaId;
  label: string;
  icon: string;
  descripcion: string;
  modulos: ModuloId[];
  // Áreas sugeridas para clasificar incidentes, horas, pruebas y documentación (G2).
  areas: string[];
  // Hitos que se crean con el proyecto (G4).
  hitos: string[];
}

// Una plantilla preselecciona módulos (editables antes de crear). Más
// adelante también trae estructura inicial (hitos, áreas) — ver G4 del plan.
export const PLANTILLAS: PlantillaProyecto[] = [
  {
    id: 'general',
    label: 'Proyecto general',
    icon: '🗂️',
    descripcion: 'Para cualquier tipo de proyecto: solo Gestión del proyecto.',
    modulos: ['gestion'],
    areas: [],
    hitos: []
  },
  {
    id: 'software',
    label: 'Desarrollo de software',
    icon: '💻',
    descripcion: 'Apps, webs o sistemas a medida. Trae hitos (análisis, diseño, desarrollo, pruebas, salida a producción) y áreas sugeridas.',
    modulos: ['gestion'],
    areas: ['Frontend', 'Backend', 'Base de datos', 'QA'],
    hitos: ['Análisis', 'Diseño', 'Desarrollo', 'Pruebas', 'Salida a producción']
  },
  {
    id: 'sap-ariba',
    label: 'Implementación SAP Ariba',
    icon: '📦',
    descripcion: 'Gestión del proyecto + SAP Ariba: soluciones, integración con el ERP y herramientas SAP.',
    modulos: ['gestion', 'sap-ariba'],
    areas: [],
    hitos: []
  }
];

// Tipo de proyecto para mostrar (T0 de plan-tickets-proyecto.md): la
// plantilla con la que se creó. Los proyectos anteriores a las plantillas
// no la tienen: con SAP Ariba son "Implementación SAP Ariba", si no
// "Proyecto general".
export const plantillaDe = (project?: { plantilla?: string; modulos?: string[] } | null): PlantillaProyecto =>
  PLANTILLAS.find((p) => p.id === project?.plantilla) ||
  PLANTILLAS.find((p) => p.id === (esProyectoSap(project) ? 'sap-ariba' : 'general'))!;

// Cómo se clasifican incidentes, horas, pruebas y documentación (G2):
//   - con SAP Ariba: por "solución relacionada" (obligatoria), como siempre;
//   - sin SAP: por "área" del proyecto (opcional, la lista la define el
//     proyecto: Frontend, Backend…). El valor se guarda en el mismo campo
//     `solucionRelacionada` de cada registro, para no duplicar lógica.
export interface Clasificacion {
  modoArea: boolean;
  opciones: string[];
}
export const clasificacionDe = (project?: { modulos?: string[]; solucionesActivas?: string[]; areas?: string[] } | null): Clasificacion =>
  esProyectoSap(project)
    ? { modoArea: false, opciones: project?.solucionesActivas || ['commerce'] }
    : { modoArea: true, opciones: project?.areas || [] };

// Etiqueta de "Especificaciones Técnicas" fuera de SAP.
export const etiquetaDocumentacion = (project?: { modulos?: string[] } | null) =>
  esProyectoSap(project) ? 'Especificaciones Técnicas' : 'Documentación';
